import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartLegend,
  ChartTooltip,
  type ChartConfig,
} from '@/components/ui/chart';
import { t } from '@/i18n';
import type { FinancialMonth } from '@ip/shared-types';
import { ChartCard } from './ChartCard';
import { MONTH_LABELS, PlnDeltaTooltip, formatAxis, orderedLegendContent } from './chart-utils';

interface CashFlowChartProps {
  months: FinancialMonth[];
  year: number;
}

export function CashFlowChart({ months, year }: CashFlowChartProps) {
  const chartData = months.map((m) => ({
    month: MONTH_LABELS[m.month - 1],
    cashIn: m.cashInNet,
    cashOut: m.cashOutNet,
    net: m.cashInNet - m.cashOutNet,
  }));

  const config: ChartConfig = {
    cashIn: { label: t('dashboard.financial.cashIn'), color: 'var(--chart-2)' },
    cashOut: { label: t('dashboard.financial.cashOut'), color: 'var(--chart-3)' },
  };

  return (
    <ChartCard title={t('dashboard.financial.cashFlow')}>
      <ChartContainer config={config} className="h-[300px] w-full">
        <BarChart data={chartData} barGap={2}>
          <CartesianGrid vertical={false} strokeOpacity={0.4} />
          <XAxis dataKey="month" tickLine={false} axisLine={false} />
          <YAxis tickFormatter={formatAxis} tickLine={false} axisLine={false} width={64} />
          <ChartTooltip
            content={
              <PlnDeltaTooltip
                config={config}
                year={year}
                deltaLabel={t('dashboard.financial.cashNet')}
                deltaKey="net"
              />
            }
          />
          <ChartLegend content={orderedLegendContent(['cashIn', 'cashOut'])} />
          <Bar dataKey="cashIn" fill="var(--color-cashIn)" radius={[4, 4, 0, 0]} maxBarSize={24} />
          <Bar dataKey="cashOut" fill="var(--color-cashOut)" radius={[4, 4, 0, 0]} maxBarSize={24} />
        </BarChart>
      </ChartContainer>
    </ChartCard>
  );
}
